import { endOfDay, startOfDay } from 'date-fns';
import { useAtomValue } from 'jotai';

import { useGetBenchmarkingInsightsData } from '@/react-query/insights/useGetBenchmarkingInsightsData';

import { endDateAtom, startDateAtom } from '../../atoms';
import { usePriorPeriod } from '../datePresets';

import {
  useEnabledPilotRegionFilter,
  useMaterialityFilter,
  usePracticesFilter,
  useProviderRoleFilter,
  useTreatmentsFilter,
} from './clinicalFilters';

export const useGetFilteredClinicalData = ({
  isPilotRegionEnabled = true,
  isPracticesEnabled = true,
  isTreatmentsEnabled = true,
  isProviderRoleEnabled = true,
  isMaterialityEnabled = true,
}: {
  isPilotRegionEnabled?: boolean;
  isPracticesEnabled?: boolean;
  isTreatmentsEnabled?: boolean;
  isProviderRoleEnabled?: boolean;
  isMaterialityEnabled?: boolean;
}) => {
  const startDate = useAtomValue(startDateAtom);
  const endDate = useAtomValue(endDateAtom);
  const { priorStart, priorEnd } = usePriorPeriod();

  const { data, isLoading } = useGetBenchmarkingInsightsData({
    start: startOfDay(startDate),
    end: endOfDay(endDate),
    priorStart,
    priorEnd,
  });

  // Apply filters (order doesn't matter)
  const filteredByPilotRegion = useEnabledPilotRegionFilter(
    data,
    isPilotRegionEnabled,
  );

  const filteredByPractices = usePracticesFilter(
    filteredByPilotRegion,
    isPracticesEnabled,
  );

  const filteredByTreatments = useTreatmentsFilter(
    filteredByPractices,
    isTreatmentsEnabled,
  );

  const filteredByProviderRole = useProviderRoleFilter(
    filteredByTreatments,
    isProviderRoleEnabled,
  );

  const filteredByMateriality = useMaterialityFilter(
    filteredByProviderRole,
    isMaterialityEnabled,
  );

  return { data: filteredByMateriality, isLoading };
};
